<template>
  <div>
    <SaleEditValueGeneral></SaleEditValueGeneral>
    <Card class="sale-individual-card">
      <template #title>Vender por valor individual</template>
      <template #content>
        <Accordion v-model:activeIndex="activeIndex">
          <AccordionTab
            v-for="(saleBovine, index) in bovines"
            :key="index"
            :disabled="false"
          >
            <template #header>
              <SaleAccordionHeader
                :saleBovine="saleBovine"
              ></SaleAccordionHeader>
            </template>
            <AppForm ref="form">
              <div class="p-fluid p-formgrid p-grid">
                <div class="p-col-12">
                  <SaleBovineCard :bovine="saleBovine.bovine">
                    <template #weight>
                      <div class="p-inputgroup">
                        <InputNumber
                          :class="{ 'p-invalid': !!formErrors.weight }"
                          v-model="saleBovine.weight"
                          :minFractionDigits="2"
                          mode="decimal"
                        />
                        <Button
                          icon="pi pi-check"
                          v-tooltip.top="'Atualizar peso do bovino'"
                          @click="saveWeight(saleBovine)"
                        />
                      </div>
                    </template>
                  </SaleBovineCard>
                </div>
                <div class="p-field p-md-4 p-xl-2">
                  <AppFormItem label="Tipo de cálculo">
                    <AppAutoComplete
                      v-model="saleBovine.calculationTypeId"
                      :options="calculationOptions"
                    ></AppAutoComplete>
                  </AppFormItem>
                </div>
                <div
                  class="p-field p-md-3 p-xl-2"
                  v-if="
                    saleBovine.calculationTypeId === calculationTypes.ARROBA
                  "
                >
                  <AppFormItem
                    label="Peso da Arroba (Kg)"
                    :error="formErrors.arrobaWeight"
                  >
                    <InputNumber
                      v-model="saleBovine.arrobaWeight"
                      :minFractionDigits="2"
                      mode="decimal"
                    />
                  </AppFormItem>
                </div>
                <div class="p-field p-md-3 p-xl-2">
                  <AppFormItem label="Valor" :error="formErrors.value">
                    <InputNumber
                      v-model="saleBovine.value"
                      :minFractionDigits="2"
                      mode="currency"
                      currency="BRL"
                      locale="pt-BR"
                    />
                  </AppFormItem>
                </div>
                <div class="p-field p-md-3 p-xl-2">
                  <AppFormItem label="Valor Total">
                    <InputNumber
                      disabled
                      v-model="totalValueByBovine[saleBovine.bovineId]"
                      :minFractionDigits="2"
                      mode="currency"
                      currency="BRL"
                      locale="pt-BR"
                    />
                  </AppFormItem>
                </div>
              </div>
              <div class="p-sm-12">
                <div class="p-grid p-jc-end p-mt-2">
                  <div style="padding-right: 8px">
                    <Button
                      v-if="activeIndex !== 0"
                      label="Anterior"
                      class="p-button-outlined p-mr-2"
                      @click="activeIndex -= 1"
                    />
                    <Button
                      v-if="activeIndex !== bovines.length - 1"
                      label="Próximo"
                      @click="nextBovine(saleBovine)"
                    />
                  </div>
                </div>
              </div>
            </AppForm>
          </AccordionTab>
        </Accordion>
      </template>
    </Card>
    <div class="p-sm-12">
      <div class="p-grid p-jc-end p-mt-2">
        <div style="padding-right: 8px">
          <Button
            label="Voltar"
            class="p-button-outlined p-mr-2"
            @click="prevPage()"
          />
          <Button label="Avançar" @click="nextPage()" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import debounce from 'debounce';
import { mapFields } from 'vuex-map-fields';
import { mapActions, mapGetters } from 'vuex';
import { calculationTypes } from '../../lib/sales';
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';
import SaleBovineCard from './components/SaleBovineCard';
import SaleAccordionHeader from './components/SaleAccordionHeader.vue';
import SaleEditValueGeneral from './SaleEditValueGeneral.vue';

export default {
  name: 'SaleEditValue',
  components: {
    Accordion,
    AccordionTab,
    SaleBovineCard,
    SaleAccordionHeader,
    SaleEditValueGeneral,
  },
  data() {
    return {
      calculationTypes,
      activeIndex: 0,
      calculationOptions: [
        { id: 1, name: 'Cálculo por kg' },
        { id: 2, name: 'Cálculo por arroba' },
        { id: 3, name: 'Valor fixo' },
      ],
      formErrors: {},
    };
  },
  computed: {
    ...mapFields('sale', ['formData.buyer', 'formData.bovines']),
    ...mapGetters('sale', [
      'totalValueByBovine',
      'isValueStepValid',
      'totalWeight',
    ]),
  },
  created() {
    this.activeIndex = 0;
    if (!this.buyer.name) {
      this.$emit('change-page', { index: 0 });
    }
  },
  methods: {
    ...mapActions('sale', ['addBovines', 'removeBovine', 'saveBovineWeight']),
    nextPage() {
      if (this.validateAll()) {
        this.$emit('next-page', { pageIndex: 2 });
      }
    },
    prevPage() {
      this.$emit('prev-page', { pageIndex: 2 });
    },
    nextBovine(saleBovine) {
      if (this.validate(saleBovine)) {
        this.activeIndex += 1;
      }
    },
    validateAll() {
      const invalidIndex = this.bovines.findIndex(
        (bovine) => !this.validate(bovine)
      );
      if (invalidIndex >= 0) {
        this.activeIndex = invalidIndex;
        return false;
      }
      return true;
    },
    validate(saleBovine = {}) {
      this.formErrors = {};
      if (!saleBovine.value) {
        this.formErrors.value = { message: 'Informe o valor' };
      }

      if (
        !saleBovine.weight &&
        [calculationTypes.KG, calculationTypes.ARROBA].includes(
          saleBovine.calculationTypeId
        )
      ) {
        this.formErrors.weight = { message: 'Informe o peso' };
      }

      if (
        !saleBovine.arrobaWeight &&
        saleBovine.calculationTypeId === calculationTypes.ARROBA
      ) {
        this.formErrors.arrobaWeight = { message: 'Informe o peso da arroba' };
      }
      return !Object.keys(this.formErrors).length;
    },
    saveWeight: debounce(
      async function saveWeight(saleBovine) {
        await this.saveBovineWeight(saleBovine);
        this.$toast.add({
          severity: 'success',
          summary: 'Atualizado com sucesso!',
          detail: 'O peso do bovino foi atualizado.',
          life: 3000,
        });
      },
      500,
      true
    ),
  },
  async beforeRouteLeave(to, from, next) {
    if (to.name === 'sale.create.review' && !this.validateAll()) {
      this.$toast.add({
        severity: 'warn',
        summary: 'Atenção!',
        detail: 'Preencha os valores para avançar!',
        life: 3000,
      });
      next(false);
    } else {
      next();
    }
  },
};
</script>

<style scoped>
div >>> .p-accordion-header.p-disabled {
  opacity: unset !important;
  cursor: default !important;
}

.sale-individual-card >>> .p-card-body {
  padding: 0;
}

.sale-individual-card >>> .p-card-content {
  padding-bottom: 0;
}

.sale-individual-card >>> .p-card-title {
  padding: 14px 0 0 14px;
}
</style>
